import {
  SelectField,
  TextareaField,
  MonthlyTargetGrid,
} from "@/components/common";
import { Input } from "@/components/ui/input";
import { useTran } from "@/hooks";
import { getDisclosureYearValueOptions } from "@/features/esg/utils/year-helpers";

const normalizeNumberInput = (value) => {
  const raw = String(value ?? "");
  if (raw === "") return "";
  const normalized = raw.replace(/[^\d.-]/g, "").replace(/(?!^)-/g, "");
  return normalized.replace(/(\..*)\./g, "$1");
};

const handleKeyDown = (event) => {
  const { key } = event;
  if (
    [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      ".",
      "-",
    ].includes(key)
  )
    return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(key.toLowerCase())
  )
    return;
  if (!/^\d$/.test(key)) event.preventDefault();
};

const TargetValueFields = ({
  disclosure,
  values = {},
  onChange,
  disabled = false,
  inputType = "",
  unitOptions = [],
  isMonthly = false,
  monthOptions,
}) => {
  const { t } = useTran();
  const unit = disclosure?.defaultUnit;
  const valueYearOptions = getDisclosureYearValueOptions(disclosure);

  if (isMonthly) {
    return (
      <MonthlyTargetGrid
        months={values.months ?? {}}
        onChange={(newMonths) => {
          const nextValues = { ...values, months: newMonths };
          delete nextValues.value;
          delete nextValues.monthlyValues;
          onChange?.(nextValues);
        }}
        inputType={inputType}
        unitOptions={unitOptions}
        monthOptions={monthOptions}
        disabled={disabled}
        className="pt-1"
      />
    );
  }

  const isNumberInput =
    inputType !== "simpleLongText" &&
    inputType !== "selector" &&
    inputType !== "simple";

  const handleChange = (val) => {
    const nextValues = {
      ...values,
      value: isNumberInput ? normalizeNumberInput(val) : val,
    };
    delete nextValues.months;
    delete nextValues.monthlyValues;
    onChange?.(nextValues);
  };

  return (
    <div className="flex items-center gap-2">
      {valueYearOptions.length > 0 ? (
        <SelectField
          options={valueYearOptions}
          value={values.value ?? ""}
          onValueChange={handleChange}
          placeholder={t("Select year")}
          disabled={disabled}
          className="w-40"
        />
      ) : inputType === "simpleLongText" ? (
        <TextareaField
          value={values.value ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={t("Response")}
          className="w-60"
          textareaClassName="text-sm min-h-[4rem] resize-y"
          disabled={disabled}
        />
      ) : inputType === "selector" ? (
        <SelectField
          options={unitOptions}
          value={values.value ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={t("Select")}
          disabled={unitOptions.length === 0 || disabled}
          className="w-40"
        />
      ) : inputType === "simple" ? (
        <Input
          type="text"
          placeholder={t("Response")}
          value={values.value ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          className="w-40"
        />
      ) : (
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          placeholder={t("e.g. 100")}
          value={values.value ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="w-40"
        />
      )}

      {unit ? (
        <span className="w-40 text-center text-sm text-muted-foreground">
          {unit}
        </span>
      ) : null}
    </div>
  );
};

export default TargetValueFields;
